import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { OPERATIONS, type OperationId } from '@visualitics-toolkit/shared';

import { AppShell } from './components/templates/AppShell';
import { OperationLayout } from './components/templates/OperationLayout';
import { LoadingBlock } from './components/molecules';
import { HomePage } from './pages/HomePage';
import { DbInsightsPage } from './pages/DbInsightsPage';
import { LoginPage } from './pages/LoginPage';
import { LogsPage } from './pages/LogsPage';
import { RunDetailPage, RunsPage } from './pages/RunsPage';
import { ProviderCollectionsPage } from './pages/ProviderCollectionsPage';
import { SettingsPage } from './pages/SettingsPage';
import { SqlLibraryPage } from './pages/SqlLibraryPage';
import { api } from './lib/api-client';
import { queryClient } from './lib/query-client';
import { queryKeys } from './lib/queries';
import type { SessionUserDto } from '@visualitics-toolkit/shared';

const rootRoute = createRootRoute({ component: () => <Outlet /> });

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

/** Tudo abaixo daqui exige sessão: sem cookie válido, o loader redireciona para /login. */
const authedRoute = createRoute({
  id: 'authed',
  getParentRoute: () => rootRoute,
  beforeLoad: async () => {
    try {
      await queryClient.ensureQueryData({
        queryKey: queryKeys.session,
        queryFn: () => api.get<SessionUserDto>('/auth/session'),
      });
    } catch {
      throw redirect({ to: '/login' });
    }
  },
  pendingComponent: () => <LoadingBlock label="Verificando sessão…" />,
  component: () => (
    <AppShell>
      <Outlet />
    </AppShell>
  ),
});

const homeRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/',
  component: HomePage,
});

/** Uma rota por operação, gerada do registry — a UI não mantém lista paralela. */
const operationRoutes = (Object.keys(OPERATIONS) as OperationId[])
  .map((id) => OPERATIONS[id])
  .filter((operation) => operation.route !== null)
  .map((operation) =>
    createRoute({
      getParentRoute: () => authedRoute,
      path: operation.route as string,
      component: () => <OperationLayout operationId={operation.id} />,
      validateSearch: (search: Record<string, unknown>) => ({
        sqlLibraryItemId:
          typeof search.sqlLibraryItemId === 'string' ? search.sqlLibraryItemId : undefined,
      }),
    }),
  );

const sqlLibraryRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/sql-library',
  component: SqlLibraryPage,
});

const runsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/runs',
  component: RunsPage,
});

const runDetailRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/runs/$runId',
  component: function RunDetail() {
    const { runId } = runDetailRoute.useParams();
    return <RunDetailPage runId={runId} />;
  },
});

const logsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/logs',
  component: LogsPage,
});

const dbInsightsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/db-insights',
  component: DbInsightsPage,
});

const providerCollectionsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/provider-collections',
  component: ProviderCollectionsPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/settings',
  component: SettingsPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  authedRoute.addChildren([
    homeRoute,
    ...operationRoutes,
    sqlLibraryRoute,
    runsRoute,
    runDetailRoute,
    logsRoute,
    dbInsightsRoute,
    providerCollectionsRoute,
    settingsRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPendingComponent: () => <LoadingBlock />,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
